import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { formatTimeWithUnits } from '@shared/utils/FormatTime';

interface CampaignNavigationDialogProps {
  open: boolean;
  isSaving: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSave: () => void | Promise<void>;
}

interface EditCampaignDialogsProps {
  showScheduleWarningDialogFlag: boolean;
  onCloseScheduleDialog: () => void;
  setShowScheduleWarningDialogFlag: React.Dispatch<React.SetStateAction<boolean>>;
  timeUntilStartCampaignRun: number;
  onRunCampaign: () => void | Promise<void>;
  showUsageLimitWarningDialog: boolean;
  setShowUsageLimitWarningDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onUsageLimitWarningConfirm: () => void | Promise<void>;
  showUsageLimitBlockedDialog: boolean;
  setShowUsageLimitBlockedDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showUsageLimitVerifyFailedDialog: boolean;
  setShowUsageLimitVerifyFailedDialog: React.Dispatch<React.SetStateAction<boolean>>;
  campaignNavigation: CampaignNavigationDialogProps;
}

export const EditCampaignDialogs: React.FC<EditCampaignDialogsProps> = ({
  showScheduleWarningDialogFlag,
  onCloseScheduleDialog,
  setShowScheduleWarningDialogFlag,
  timeUntilStartCampaignRun,
  onRunCampaign,
  showUsageLimitWarningDialog,
  setShowUsageLimitWarningDialog,
  onUsageLimitWarningConfirm,
  showUsageLimitBlockedDialog,
  setShowUsageLimitBlockedDialog,
  showUsageLimitVerifyFailedDialog,
  setShowUsageLimitVerifyFailedDialog,
  campaignNavigation,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        showDialogFlag={showScheduleWarningDialogFlag}
        handleClose={onCloseScheduleDialog}
        dialogTitle={'dialog.title4'}
        dialogContentText={t('dialog.content4', {
          timeToStart: formatTimeWithUnits(timeUntilStartCampaignRun),
        })}
        variant="warning"
        showDialog={setShowScheduleWarningDialogFlag}
        confirmDialog={onRunCampaign}
      />
      <ConfirmDialog
        showDialogFlag={showUsageLimitWarningDialog}
        handleClose={() => setShowUsageLimitWarningDialog(false)}
        dialogTitle={'dialog.usageLimitWarningTitle'}
        dialogContentText={t('dialog.usageLimitWarningContent')}
        showDialog={setShowUsageLimitWarningDialog}
        confirmDialog={onUsageLimitWarningConfirm}
      />
      <Dialog
        open={showUsageLimitBlockedDialog}
        onClose={() => setShowUsageLimitBlockedDialog(false)}
        aria-labelledby="usage-limit-blocked-title"
        aria-describedby="usage-limit-blocked-description">
        <DialogTitle id="usage-limit-blocked-title">
          {t('dialog.usageLimitBlockedTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="usage-limit-blocked-description">
            {t('dialog.usageLimitBlockedContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUsageLimitBlockedDialog(false)}>
            {t('adminMainPage.operation.ok')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showUsageLimitVerifyFailedDialog}
        onClose={() => setShowUsageLimitVerifyFailedDialog(false)}
        aria-labelledby="usage-limit-verify-failed-title"
        aria-describedby="usage-limit-verify-failed-description">
        <DialogTitle id="usage-limit-verify-failed-title">
          {t('dialog.usageLimitVerifyFailedTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="usage-limit-verify-failed-description">
            {t('dialog.usageLimitVerifyFailedContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUsageLimitVerifyFailedDialog(false)}>
            {t('adminMainPage.operation.ok')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={campaignNavigation.open}
        onClose={() => {
          if (!campaignNavigation.isSaving) campaignNavigation.onClose();
        }}
        aria-labelledby="campaign-unsaved-dialog-title"
        aria-describedby="campaign-unsaved-dialog-description">
        <DialogTitle id="campaign-unsaved-dialog-title">
          {t('adminMainPage.messages.unsavedChanges')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="campaign-unsaved-dialog-description">
            {t('dialog.content2')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={campaignNavigation.onClose} disabled={campaignNavigation.isSaving}>
            {t('action.cancel')}
          </Button>
          <Button onClick={campaignNavigation.onDiscard} disabled={campaignNavigation.isSaving}>
            {t('common.discard')}
          </Button>
          <Button
            onClick={() => void campaignNavigation.onSave()}
            variant="contained"
            disabled={campaignNavigation.isSaving}>
            {campaignNavigation.isSaving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('action.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
